/**
 * Main menu component.
 * Displays the application's main navigation menu with collapsible content,
 * login section for anonymous users, and top bar. Handles menu visibility based on
 * current page and focus state, with animation transitions.
 */

import CollapseBar from '@components/atoms/container/collapseBar';
import CollapseContainer from '../../atoms/container/menuCollapseContainer';
import CollapseIcon from '../../atoms/images/menuCollapse';
import Content from '../../elements/menu/menuContent';
import ContentContainer from '../../atoms/container/menuContentContainer';
import Focus from '../../../../shared/types/enums/focus';
import HorizontalAnimation from '@components/atoms/container/horizontalAnimationContainer';
import Login from '../../elements/menu/menuLogin';
import MenuContainer from '@components/atoms/container/menuContainer';
import Pages from '@pages';
import TopBar from '../../elements/topBar/topBar';
import useUpdateMenu from '../../../../shared/components/utils/hooks/useUpdateMenu';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { routerSelector } from '../../../../shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useEffect, useRef, useState } from 'react';
import { userSelector } from '../../../../shared/store/slices/user.slice';
import {
  focusSelector,
  pushFocus,
  setFocus,
} from '../../../../shared/store/slices/focus.slice';

/**
 * Main menu component that manages menu visibility and animations.
 * Displays menu content, login section, and handles focus-based animations.
 *
 * @returns {JSX.Element | null} Menu component or null if not initialized
 */
function Menu() {
  const dispatch = useAppDispatch();
  const { focus, stack } = useAppSelector(focusSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { initialized, isAnonymous } = useAppSelector(userSelector);
  const {
    route: { Page },
  } = useAppSelector(routerSelector);

  const [animation, setAnimation] = useState({
    content: 'menu-content-load',
    collapse: 'menu-fade-out',
  });
  const [display, setDisplay] = useState(false);
  const [startup, setStartup] = useState(true);

  const prevFocusRef = useRef(focus);

  // Menu update hook

  const { data, index } = useUpdateMenu(display);

  // Actions

  const onClick = () => {
    if (!display) return;
    const { focus: focusSlice } = store.getState();
    if (focusSlice.focus !== Focus.MENU) {
      dispatch(pushFocus(Focus.MENU));
    }
  };

  const onMouseEnter = () => {
    if (!display) return;
    const { focus: focusSlice } = store.getState();
    if (focusSlice.focus !== Focus.MENU) {
      dispatch(pushFocus(Focus.MENU));
    }
  };

  // Effect hooks

  useEffect(() => {
    if (modal) {
      return;
    }
    if (
      startup ||
      [Focus.ADS, Focus.MODAL_BUTTONS, Focus.SPLASH].includes(focus as number)
    ) {
      return;
    }

    // Display menu content
    if (
      focus === Focus.MENU ||
      (focus === Focus.LOGIN && stack.includes(Focus.MENU)) ||
      (focus === Focus.MODAL_INPUT &&
        (stack.includes(Focus.MENU) || stack.includes(Focus.LOGIN)))
    ) {
      setAnimation({
        collapse: 'menu-collapse-out',
        content:
          prevFocusRef.current === -1 ? 'menu-content-load' : 'menu-content-in',
      });
    }

    // Display collapse
    else {
      setAnimation({
        collapse: display ? 'menu-collapse-in' : 'menu-collapse-out',
        content: 'menu-content-out',
      });
    }
    prevFocusRef.current = focus;
  }, [focus, modal, stack, startup]);

  useEffect(() => {
    switch (Page) {
      // Filter pages that should include menu
      case Pages.ACCOUNT:
      case Pages.ADDON:
      case Pages.CHANNEL_GUIDE:
      case Pages.CLASSIC_CHANNEL:
      case Pages.CHANNEL_LIST:
      case Pages.CHANNELS_MAIN:
      case Pages.CHANNELS_CHOICE:
      case Pages.CORNER:
      case Pages.DETAIL:
      case Pages.DISK:
      case Pages.EMPTY:
      case Pages.EXIT:
      case Pages.IA_CORNER:
      case Pages.INITIAL:
      case Pages.LAST_SHOWS_CHANNEL:
      case Pages.LOGOUT:
      case Pages.MANAGE_CHANNELS:
      case Pages.MORE_CONTENT:
      case Pages.MOVIES:
      case Pages.MY_SPACE:
      case Pages.RECORDINGS:
      case Pages.SEARCH:
      case Pages.SEASON_EXTENDED:
      case Pages.SERIES_EXTENDED:
      case Pages.SERIES_U7D:
      case Pages.POWER_SAVING:
      case Pages.SETTINGS:
      case Pages.SETTING_LANGUAGES:
      case Pages.SHOWS_U7D:
      case Pages.WATCH_EVERYTHING:
      case Pages.BACK_DOOR:
        setDisplay(true);
        setStartup((prev) => {
          prev && setTimeout(() => dispatch(setFocus(Focus.MENU)));
          return false;
        });
        break;
      // Otherwise, do not display menu
      default:
        setDisplay(false);
    }
  }, [Page]);

  return initialized && data.length ? (
    <MenuContainer
      animation={display ? 'menu-fade-in' : ''}
      style={{ opacity: display ? 1 : 0 }}
    >
      {isAnonymous ? <Login /> : null}
      <TopBar />
      <HorizontalAnimation animation={animation.content} force>
        <ContentContainer>
          <Content
            Data={data}
            display={animation.content === 'menu-content-out'}
            index={index}
          />
        </ContentContainer>
        <CollapseContainer
          animation={animation.collapse}
          force={true}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
        >
          <CollapseIcon force={true} />
          <CollapseBar />
        </CollapseContainer>
      </HorizontalAnimation>
    </MenuContainer>
  ) : null;
}

export default Menu;
